import React from "react";
import ReactInputVerificationCode from "react-input-verification-code";
import "./BaseOtpInput.css";

/**
 * BaseOtpInput — the canonical reskin OTP / verification-code field: a joined
 * row of single-character cells (used by ForgotPassword/VerificationCode and
 * the Verify email-confirmation screen).
 *
 * WHAT IT IS: the visual styling (40×40 cells, shared borders, radius only on
 * the first/last cell, --input-content-filled Inter text) layered over the input behavior.
 * It owns ONLY the cells; the label and any validation rows are the caller's.
 *
 * WHAT IT WRAPS: `react-input-verification-code`, scoped under a
 * `.base-otp-input` div so BaseOtpInput.css can target the library's cells.
 *
 * CONTRACT (props in):
 *  - length: number of cells (default 6).
 *  - value: controlled value (optional).
 *  - onChange: (code) => void — fires on every keystroke. The raw library
 *      value includes the "-" placeholder for empty cells; callers that want
 *      the digits only should strip it (`code.replace(/-/g, "")`).
 *  - onCompleted: (code) => void — fires once all cells are filled.
 *  - placeholder: per-cell placeholder char (default "-").
 *  - autoFocus, type, passwordMask: forwarded to the library.
 *  - "data-testid" / dataTestId: testid on the wrapper.
 *  - className: extra classes merged onto the wrapper.
 *
 * BEHAVIOR (out): same input behavior as the bare library, restyled to the
 * reskin spec.
 *
 * MIGRATION NOTE (shadcn): this becomes shadcn's <InputOTP> with N
 * <InputOTPSlot>s. The wrapper + BaseOtpInput.css go away; onChange/onCompleted
 * map to <InputOTP onChange> + a length check. The OTP token block in tokens.js
 * carries the geometry across.
 */
const BaseOtpInput = ({
  length = 6,
  value,
  onChange,
  onCompleted,
  placeholder = "-",
  autoFocus,
  type,
  passwordMask,
  className,
  dataTestId,
  "data-testid": dataTestIdProp,
  ...props
}) => {
  const testId = dataTestIdProp ?? dataTestId;

  return (
    <div
      className={["base-otp-input", className].filter(Boolean).join(" ")}
      data-testid={testId}
      {...props}
    >
      <ReactInputVerificationCode
        length={length}
        value={value}
        onChange={onChange}
        onCompleted={onCompleted}
        placeholder={placeholder}
        autoFocus={autoFocus}
        type={type}
        passwordMask={passwordMask}
      />
    </div>
  );
};

export default BaseOtpInput;
